import {
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  TimerOff as TimerOffIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { ReservationConfigWithDeposit } from "../../apis/reservations/apis";
import {
  minutesToTimeDelta,
  timeDeltaToMinutes,
} from "../../domain/reservations";

const STRIPE_MINIMUM_AMOUNTS: Record<string, number> = {
  usd: 0.5,
  aed: 2.0,
  aud: 0.5,
  bgn: 1.0,
  brl: 0.5,
  cad: 0.5,
  chf: 0.5,
  czk: 15.0,
  dkk: 2.5,
  eur: 0.5,
  gbp: 0.3,
  hkd: 4.0,
  huf: 175.0,
  inr: 0.5,
  jpy: 50,
  mxn: 10.0,
  myr: 2.0,
  nok: 3.0,
  nzd: 0.5,
  pln: 2.0,
  ron: 2.0,
  sek: 3.0,
  sgd: 0.5,
  thb: 10.0,
};

const getStripeMinimum = (currency: string | null): number | null => {
  if (!currency) return null;
  return STRIPE_MINIMUM_AMOUNTS[currency.toLowerCase()] ?? null;
};

interface ReservationsFormProps {
  config: ReservationConfigWithDeposit;
  currency: string | null;
  onSave: (config: ReservationConfigWithDeposit) => Promise<void>;
}

interface FormValues {
  reservationDurationMinutes: number;
  depositAmountMajor: number;
  cancellationWindowMinutes: number;
  noShowGracePeriodMinutes: number;
}

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}> = ({ icon, title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
    <Box sx={{ color: "primary.main", mt: 0.5 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

const FieldWithTooltip: React.FC<{
  children: React.ReactNode;
  tooltip: string;
  fitContent?: boolean;
}> = ({ children, tooltip, fitContent }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
    <Box sx={fitContent ? undefined : { flex: 1 }}>{children}</Box>
    <Tooltip title={tooltip} placement="top">
      <IconButton
        size="small"
        sx={{
          mt: 0.5,
          opacity: 0.5,
          "&:hover": { opacity: 1 },
        }}
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

export const ReservationsForm: React.FC<ReservationsFormProps> = ({
  config,
  currency,
  onSave,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      reservationDurationMinutes: timeDeltaToMinutes(
        config.max_future_reservation_start_datetime
      ),
      depositAmountMajor: (config.deposit?.amount_minor || 0) / 100,
      cancellationWindowMinutes: timeDeltaToMinutes(
        config.allow_cancel_reservation_before_start_datetime
      ),
      noShowGracePeriodMinutes: timeDeltaToMinutes(config.no_show_grace_period),
    },
  });

  const handleFormSubmit = async (values: FormValues) => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const durationTimeDelta = minutesToTimeDelta(
        values.reservationDurationMinutes
      );
      await onSave({
        ...config,
        max_future_reservation_start_datetime: durationTimeDelta,
        disallow_vehicle_from_being_rented_before_start_datetime:
          durationTimeDelta,
        allow_cancel_reservation_before_start_datetime: minutesToTimeDelta(
          values.cancellationWindowMinutes
        ),
        no_show_grace_period: minutesToTimeDelta(
          values.noShowGracePeriodMinutes
        ),
        deposit: currency
          ? {
              amount_minor: Math.round(values.depositAmountMajor * 100),
              currency: currency,
            }
          : null,
      });
      setSuccess(true);
    } catch (e) {
      setError("Failed to save changes");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: "720px" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Changes saved successfully
        </Alert>
      )}

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <SectionHeader
            icon={<AccessTimeIcon />}
            title="Timing Settings"
            subtitle="Configure how reservations work"
          />

          <Grid container={true} spacing={3}>
            <Grid item={true} xs={12} sm={6}>
              <FieldWithTooltip tooltip="How far in advance users can reserve a vehicle. During this time, the vehicle is blocked and unavailable for other users to rent.">
                <Controller
                  name="reservationDurationMinutes"
                  control={control}
                  rules={{
                    required: "Required",
                    min: { value: 5, message: "Min 5 minutes" },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reservation Window"
                      type="number"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">min</InputAdornment>
                        ),
                      }}
                      error={!!errors.reservationDurationMinutes}
                      helperText={errors.reservationDurationMinutes?.message}
                      fullWidth={true}
                    />
                  )}
                />
              </FieldWithTooltip>
            </Grid>

            <Grid item={true} xs={12} sm={6}>
              <FieldWithTooltip tooltip="Users can cancel without penalty up until this many minutes before the reservation starts.">
                <Controller
                  name="cancellationWindowMinutes"
                  control={control}
                  rules={{
                    required: "Required",
                    min: { value: 0, message: "Cannot be negative" },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Free Cancellation"
                      type="number"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CancelIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            min before
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.cancellationWindowMinutes}
                      helperText={errors.cancellationWindowMinutes?.message}
                      fullWidth={true}
                    />
                  )}
                />
              </FieldWithTooltip>
            </Grid>

            <Grid item={true} xs={12} sm={6}>
              <FieldWithTooltip tooltip="Time after the reservation start when the user will be marked as a no-show if they haven't started the rental.">
                <Controller
                  name="noShowGracePeriodMinutes"
                  control={control}
                  rules={{
                    required: "Required",
                    min: { value: 0, message: "Cannot be negative" },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="No-Show Grace Period"
                      type="number"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimerOffIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            min after
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.noShowGracePeriodMinutes}
                      helperText={errors.noShowGracePeriodMinutes?.message}
                      fullWidth={true}
                    />
                  )}
                />
              </FieldWithTooltip>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <SectionHeader
            icon={<PaymentIcon />}
            title="Deposit"
            subtitle="Amount held when a reservation is created"
          />

          <FieldWithTooltip
            tooltip="This amount is held on the user's payment method when they create a reservation. It's released when the reservation is completed or cancelled within the free cancellation window."
            fitContent={true}
          >
            <Controller
              name="depositAmountMajor"
              control={control}
              rules={{
                required: "Required",
                min: { value: 0, message: "Cannot be negative" },
                validate: (value) => {
                  const minimum = getStripeMinimum(currency);
                  if (minimum !== null && value > 0 && value < minimum) {
                    return `Minimum ${minimum} ${currency?.toUpperCase()} or 0`;
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Deposit Amount"
                  type="number"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PaymentIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: currency ? (
                      <InputAdornment position="end">{currency}</InputAdornment>
                    ) : undefined,
                  }}
                  disabled={!currency}
                  error={!!errors.depositAmountMajor}
                  helperText={
                    !currency
                      ? "No payment configuration found for this city"
                      : errors.depositAmountMajor?.message
                  }
                  sx={{ maxWidth: 280 }}
                />
              )}
            />
          </FieldWithTooltip>

          {!currency && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Set up payment configuration to enable deposits
            </Alert>
          )}
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={saving || !isDirty}
            sx={{ minWidth: 140 }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
